import type {
  ClientExportCSVEvent,
  ClientsPageAnalytics,
  ClientTabLabelEvent,
  SelectorValue,
} from "@partners/ui";

import { useSegmentAnalytics } from "./useSegmentAnalytics";

export const useClientsPageAnalytics = (): ClientsPageAnalytics => {
  const {
    trackButtonClickEvent,
    trackInputChangeEvent,
    trackHoverEvent,
    trackOptionClickEvent,
  } = useSegmentAnalytics();

  const trackTableRebateSelectEvent = (selectorValue: SelectorValue) =>
    trackButtonClickEvent({
      label: "Rebate account selector",
      location: "Clients table",
      extraProperties: { selector_value: selectorValue },
    });

  const trackTableRebateSelectEventV2 = (
    label: ClientTabLabelEvent,
    selected: string[],
  ) =>
    trackOptionClickEvent({
      label,
      location: "Clients",
      extraProperties: { selected },
    });

  const trackTableSearchEvent = (searchQuery: string) =>
    trackButtonClickEvent({
      label: "Search",
      location: "Clients table",
      extraProperties: { search_query: searchQuery },
    });

  const trackTableSearchFieldChangeEvent = (
    label:
      | "Account report"
      | "Trading report"
      | "Funded not traded"
      | "Approved not funded",
    searchQuery: string,
  ) =>
    trackInputChangeEvent({
      label,
      location: "Clients",
      extraProperties: { input_value: searchQuery, link_text: "Search field" },
    });

  const trackTableExportCSVEvent = ({
    label,
    searchQuery,
    selectorValue,
    fromDate,
    toDate,
  }: ClientExportCSVEvent) =>
    trackButtonClickEvent({
      label,
      location: "Clients",
      extraProperties: {
        search_query: searchQuery,
        selector_value: selectorValue,
        to_date: toDate,
        from_date: fromDate,
        link_text: "Export CSV",
      },
    });

  const trackClientTabEvent = (label: ClientTabLabelEvent) =>
    trackButtonClickEvent({
      label,
      location: "Clients",
      extraProperties: {
        link_text: label,
      },
    });

  const trackTableChartEvent = (
    isMobile: boolean,
    label: ClientTabLabelEvent,
  ) => {
    if (isMobile) {
      trackButtonClickEvent({
        label,
        location: "Clients",
      });
    } else {
      trackHoverEvent({
        label,
        location: "Clients",
      });
    }
  };

  const trackDateRangeFilterEvent = (
    fromDate: string,
    toDate: string,
    label: ClientTabLabelEvent,
  ) => {
    trackOptionClickEvent({
      label,
      location: "Clients",
      extraProperties: {
        date: [fromDate, toDate],
      },
    });
  };

  const trackTableChartTradingEvent = (type: string) => {
    trackOptionClickEvent({
      label: "Trading report",
      location: "Clients",
      extraProperties: {
        "traded by": type,
      },
    });
  };

  const trackClientDateTooltipEvent = (
    isMobile: boolean,
    label: ClientTabLabelEvent,
  ) => {
    if (isMobile) {
      trackButtonClickEvent({
        label: "Tool tip modal",
        location: "Clients#" + label,
        extraProperties: { link_text: "Tool tip modal" },
      });
    } else {
      trackHoverEvent({
        label: "Tool tip bubble",
        location: "Clients#" + label,
        extraProperties: { link_text: "Tool tip bubble" },
      });
    }
  };

  return {
    trackClientTabEvent,
    trackTableRebateSelectEvent,
    trackTableRebateSelectEventV2,
    trackTableSearchEvent,
    trackTableExportCSVEvent,
    trackTableSearchFieldChangeEvent,
    trackTableChartEvent,
    trackDateRangeFilterEvent,
    trackTableChartTradingEvent,
    trackClientDateTooltipEvent,
  };
};
